import { dungApiThat, goiApi } from '../api/http';
import { mayChuTriThuc } from '../../mock/tri-thuc';
import { mockRequest, mockState } from '../../mock/transport';
import type {
  Folder,
  KhoSua,
  KhoTao,
  KnowledgeBase,
  KnowledgeDocument,
  KnowledgeDocumentDetail,
  KnowledgeFile,
  KnowledgeStats,
  LoaiTep,
  NguoiDanhBa,
  PhongBan,
  QuyenNguoiTep,
  QuyenThuMuc,
  TaiLieuTao,
  TepSua,
  TepTaiLen,
  Trang,
  TrangThaiTaiLieu,
  UrlTaiXuong,
} from '../../modules/tri-thuc/types';
import type { ThaoTacTaiLieu } from '../../modules/tri-thuc/nghiep-vu/tri-thuc';

/** Module `tri-thuc` sau Gateway (ADR-0075 mục 9); Gateway chuyển nguyên đường dẫn tới module. */
const GOC = '/api/v1/m/tri-thuc';

const ma = (id: string) => encodeURIComponent(id);

/**
 * Chạy máy chủ giả **sau** nhịp chờ và các trạng thái `?mock=` của `mockRequest`: lỗi nghiệp vụ tới
 * cùng độ trễ với lỗi thật.
 */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

function trangRong<T>(): Trang<T> {
  return { items: [], next_cursor: null };
}

export interface ThamSoKho {
  limit: number;
  cursor?: string;
  search?: string;
  department_id?: string;
}

export interface ThamSoTep {
  limit: number;
  cursor?: string;
  knowledge_base_id?: string;
  /** `root`: tệp ở gốc bộ. */
  folder_id?: string;
  search?: string;
  file_type?: LoaiTep;
}

export interface ThamSoTaiLieu {
  limit: number;
  cursor?: string;
  knowledge_base_id?: string;
  status?: TrangThaiTaiLieu;
  search?: string;
}

function bieuMau(truong: Record<string, string | undefined>, tep: File): FormData {
  const fd = new FormData();
  for (const [k, v] of Object.entries(truong)) if (v !== undefined && v !== '') fd.append(k, v);
  fd.append('file', tep, tep.name);
  return fd;
}

/**
 * Truy cập dữ liệu module tri-thuc qua Gateway. `tenant_id` lấy từ token, không bao giờ gửi tham số.
 *
 * Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ giả (`mock/tri-thuc.ts`). `?mock=filtered-empty`
 * diễn ở đây: có bộ lọc thì trả rỗng, nên view phân biệt hai kiểu rỗng bằng chính state bộ lọc của nó.
 */
export const triThucRepository = {
  // ─── Bộ tri thức ───
  danhSachKho: (q: ThamSoKho): Promise<Trang<KnowledgeBase>> => {
    if (dungApiThat()) return goiApi(`${GOC}/knowledge-bases`, { query: { ...q } });
    if (mockState() === 'filtered-empty' && (q.search || q.department_id)) return mockRequest(trangRong());
    return mockRequest(mayChuTriThuc.danhSachKho(q), trangRong());
  },

  thongKe: (): Promise<KnowledgeStats> =>
    dungApiThat()
      ? goiApi(`${GOC}/knowledge-bases/stats`)
      : mockRequest(mayChuTriThuc.thongKe(), {
        total_knowledge_bases: 0, total_files: 0, pdf_files_count: 0, departments_using_count: 0, last_updated_at: null,
        files_by_type: {}, files_by_knowledge_base: [],
      }),

  chiTietKho: (id: string): Promise<KnowledgeBase> =>
    dungApiThat() ? goiApi(`${GOC}/knowledge-bases/${ma(id)}`) : giaLap(() => mayChuTriThuc.chiTietKho(id)),

  taoKho: (du: KhoTao): Promise<KnowledgeBase> =>
    dungApiThat()
      ? goiApi(`${GOC}/knowledge-bases`, { method: 'POST', body: du })
      : giaLap(() => mayChuTriThuc.taoKho(du)),

  suaKho: (id: string, du: KhoSua): Promise<KnowledgeBase> =>
    dungApiThat()
      ? goiApi(`${GOC}/knowledge-bases/${ma(id)}`, { method: 'PATCH', body: du })
      : giaLap(() => mayChuTriThuc.suaKho(id, du)),

  xoaKho: (id: string): Promise<void> =>
    dungApiThat()
      ? goiApi(`${GOC}/knowledge-bases/${ma(id)}`, { method: 'DELETE' })
      : giaLap(() => mayChuTriThuc.xoaKho(id)),

  // ─── Thư mục ───
  danhSachThuMuc: (kbId: string): Promise<{ items: Folder[] }> =>
    dungApiThat()
      ? goiApi(`${GOC}/knowledge-bases/${ma(kbId)}/folders`)
      : mockRequest(undefined, { items: [] as Folder[] }).then((rong) => rong ?? mayChuTriThuc.danhSachThuMuc(kbId)),

  taoThuMuc: (kbId: string, du: { name: string; parent_id: string | null }): Promise<Folder> =>
    dungApiThat()
      ? goiApi(`${GOC}/knowledge-bases/${ma(kbId)}/folders`, { method: 'POST', body: du })
      : giaLap(() => mayChuTriThuc.taoThuMuc(kbId, du)),

  doiTenThuMuc: (kbId: string, fid: string, name: string): Promise<Folder> =>
    dungApiThat()
      ? goiApi(`${GOC}/knowledge-bases/${ma(kbId)}/folders/${ma(fid)}`, { method: 'PATCH', body: { name } })
      : giaLap(() => mayChuTriThuc.doiTenThuMuc(fid, name)),

  xoaThuMuc: (kbId: string, fid: string): Promise<void> =>
    dungApiThat()
      ? goiApi(`${GOC}/knowledge-bases/${ma(kbId)}/folders/${ma(fid)}`, { method: 'DELETE' })
      : giaLap(() => mayChuTriThuc.xoaThuMuc(fid)),

  quyenThuMuc: (kbId: string): Promise<{ items: QuyenThuMuc[] }> =>
    dungApiThat()
      ? goiApi(`${GOC}/knowledge-bases/${ma(kbId)}/folders/permissions`)
      : giaLap(() => mayChuTriThuc.quyenThuMuc(kbId)),

  datQuyenThuMuc: (kbId: string, fid: string, userIds: string[]): Promise<QuyenThuMuc> =>
    dungApiThat()
      ? goiApi(`${GOC}/knowledge-bases/${ma(kbId)}/folders/${ma(fid)}/permissions`, { method: 'PUT', body: { user_ids: userIds } })
      : giaLap(() => mayChuTriThuc.datQuyenThuMuc(fid, userIds)),

  // ─── Tệp ───
  danhSachTep: (q: ThamSoTep): Promise<Trang<KnowledgeFile>> => {
    if (dungApiThat()) return goiApi(`${GOC}/files`, { query: { ...q } });
    if (mockState() === 'filtered-empty' && (q.search || q.file_type)) return mockRequest(trangRong());
    return mockRequest(mayChuTriThuc.danhSachTep(q), trangRong());
  },

  /** `POST /files` multipart; trả ngay với `CHO_INDEX`, hàng đợi của module nạp sau. */
  taiTep: (du: TepTaiLen): Promise<KnowledgeFile> =>
    dungApiThat()
      ? goiApi(`${GOC}/files`, {
        method: 'POST',
        bieuMau: bieuMau({ knowledge_base_id: du.knowledge_base_id, effective_from: du.effective_from, effective_to: du.effective_to },
          du.tep),
      })
      : giaLap(() => mayChuTriThuc.taiTep({ knowledge_base_id: du.knowledge_base_id, effective_from: du.effective_from,
        ...(du.effective_to ? { effective_to: du.effective_to } : {}), tenTep: du.tep.name, kichThuoc: du.tep.size })),

  suaTep: (id: string, du: TepSua): Promise<KnowledgeFile> =>
    dungApiThat()
      ? goiApi(`${GOC}/files/${ma(id)}`, { method: 'PATCH', body: du })
      : giaLap(() => mayChuTriThuc.suaTep(id, du)),

  /** `knowledge_base_id: null` là bỏ tệp khỏi bộ. */
  chuyenBo: (id: string, kbId: string | null): Promise<KnowledgeFile> =>
    dungApiThat()
      ? goiApi(`${GOC}/files/${ma(id)}/move`, { method: 'POST', body: { knowledge_base_id: kbId } })
      : giaLap(() => mayChuTriThuc.chuyenBo(id, kbId)),

  dongBoLai: (id: string): Promise<KnowledgeFile> =>
    dungApiThat()
      ? goiApi(`${GOC}/files/${ma(id)}/reindex`, { method: 'POST' })
      : giaLap(() => mayChuTriThuc.dongBoLai(id)),

  xoaTep: (id: string): Promise<void> =>
    dungApiThat() ? goiApi(`${GOC}/files/${ma(id)}`, { method: 'DELETE' }) : giaLap(() => mayChuTriThuc.xoaTep(id)),

  /** URL ký ngắn hạn, mở ngay khi nhận; không lưu lại. */
  taiXuongTep: (id: string): Promise<UrlTaiXuong> =>
    dungApiThat() ? goiApi(`${GOC}/files/${ma(id)}/download`) : giaLap(() => mayChuTriThuc.taiXuongTep(id)),

  datPhongBanTep: (id: string, ids: string[]): Promise<KnowledgeFile> =>
    dungApiThat()
      ? goiApi(`${GOC}/files/${ma(id)}/permissions`, { method: 'PUT', body: { department_ids: ids } })
      : giaLap(() => mayChuTriThuc.datPhongBanTep(id, ids)),

  datNguoiTep: (id: string, ids: string[]): Promise<QuyenNguoiTep> =>
    dungApiThat()
      ? goiApi(`${GOC}/files/${ma(id)}/user-permissions`, { method: 'PUT', body: { user_ids: ids } })
      : giaLap(() => mayChuTriThuc.datNguoiTep(id, ids)),

  quyenNguoiTep: (kbId: string): Promise<{ items: QuyenNguoiTep[] }> =>
    dungApiThat()
      ? goiApi(`${GOC}/knowledge-bases/${ma(kbId)}/file-permissions`)
      : giaLap(() => mayChuTriThuc.quyenNguoiTep(kbId)),

  // ─── Tài liệu kiểm duyệt ───
  danhSachTaiLieu: (q: ThamSoTaiLieu): Promise<Trang<KnowledgeDocument>> => {
    if (dungApiThat()) return goiApi(`${GOC}/documents`, { query: { ...q } });
    if (mockState() === 'filtered-empty' && (q.status || q.search || q.knowledge_base_id)) return mockRequest(trangRong());
    return mockRequest(mayChuTriThuc.danhSachTaiLieu(q), trangRong());
  },

  chiTietTaiLieu: (id: string): Promise<KnowledgeDocumentDetail> =>
    dungApiThat() ? goiApi(`${GOC}/documents/${ma(id)}`) : giaLap(() => mayChuTriThuc.chiTietTaiLieu(id)),

  taoTaiLieu: (du: TaiLieuTao): Promise<KnowledgeDocumentDetail> =>
    dungApiThat()
      ? goiApi(`${GOC}/documents`, {
        method: 'POST',
        bieuMau: bieuMau({ knowledge_base_id: du.knowledge_base_id, title: du.title, content_summary: du.content_summary, note: du.note },
          du.tep),
      })
      : giaLap(() => mayChuTriThuc.taoTaiLieu({ knowledge_base_id: du.knowledge_base_id, title: du.title,
        ...(du.note ? { note: du.note } : {}), ...(du.content_summary ? { content_summary: du.content_summary } : {}),
        tenTep: du.tep.name, kichThuoc: du.tep.size })),

  /** Chuyển trạng thái; `note` cho trả nháp (tuỳ chọn) và yêu cầu sửa (bắt buộc). */
  chuyenTaiLieu: (id: string, thaoTac: ThaoTacTaiLieu, note?: string): Promise<KnowledgeDocumentDetail> =>
    dungApiThat()
      ? goiApi(`${GOC}/documents/${ma(id)}/${thaoTac}`, { method: 'POST', ...(note !== undefined ? { body: { note } } : {}) })
      : giaLap(() => mayChuTriThuc.chuyenTaiLieu(id, thaoTac, note)),

  phienBanMoi: (id: string, du: { change_note: string; content_summary?: string | null }): Promise<KnowledgeDocumentDetail> =>
    dungApiThat()
      ? goiApi(`${GOC}/documents/${ma(id)}/versions`, { method: 'POST', body: du })
      : giaLap(() => mayChuTriThuc.phienBanMoi(id, du)),

  taiXuongTaiLieu: (id: string): Promise<UrlTaiXuong> =>
    dungApiThat() ? goiApi(`${GOC}/documents/${ma(id)}/download`) : giaLap(() => mayChuTriThuc.taiXuongTaiLieu(id)),

  // ─── Danh bạ của tổ chức (đọc từ DB lõi qua module, ADR-0075 mục 5) ───
  phongBan: (): Promise<{ items: PhongBan[] }> =>
    dungApiThat() ? goiApi(`${GOC}/directory/departments`) : giaLap(() => mayChuTriThuc.phongBan()),

  nguoiTheoPhongBan: (deptId: string, cursor?: string): Promise<Trang<NguoiDanhBa>> =>
    dungApiThat()
      ? goiApi(`${GOC}/directory/users`, { query: { department_id: deptId, limit: 100, cursor } })
      : giaLap(() => mayChuTriThuc.nguoiTheoPhongBan(deptId)),
};
